
  import { computed, defineComponent, ref, watch, onMounted } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { useRouter, useRoute } from 'vue-router';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useStore } from 'vuex';
  import { useI18n } from 'vue-i18n/index';
  import Multiselect from '@vueform/multiselect';
  import { useAbility } from '@casl/vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';

  export default defineComponent({
    name: 'auctions-properties-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
      Multiselect,
    },
    setup() {
      const submitButtonRef = ref<null | HTMLButtonElement>(null);
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { t, te } = useI18n();
      const { can } = useAbility();
      const selectedAuctionLocation = ref<number>(0);
      const selectedAuctionDescription = ref<number>(0);
      const showFileInput = ref(true);
      const submitPullButton = ref<HTMLButtonElement | null>(null);

      const property = ref<any | null>({
        id: 0,
        propertyId: 0,
        block: 0,
        plot: 0,
        paciNo: 0,
        longitude: 0,
        latitude: 0,
        venue: 0,
        size: 0,
        startingPrice: 0,
        startingPricePerSqrM: 0,
        supplyPercentage: 0,
        statusId: 0,
        salePrice: 0,
        salePricePerSqrM: 0,
        percentagePremiumPaid: 0,
        isActive: true,
        additionalInfoEn: '',
        additionalInfoAr: '',
        createdAt: '',
        areaName: 0,
        locationId: 0,
        descriptionId: 0,
      });

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('editProperty'), [
          translate('auctions'),
          translate('properties'),
        ]);

        await loadPropertyData();
        await loadAllData();
      });

      const loadPropertyData = async () => {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');

        const { data } = await store.dispatch(
          Actions.GET_PROPERTY_DETAILS,
          route.params.id
        );
        property.value = data.data;

        selectedAuctionLocation.value =
          property.value.auctionsLocation?.id ?? null;
        selectedAuctionDescription.value =
          property.value.propertyDescription?.id ?? null;

        if (property.value.startingPrice) {
          const input = property.value.startingPrice.replace(/,/g, '');
          const numericValue = parseFloat(input);
          if (!isNaN(numericValue)) {
            property.value.startingPrice = new Intl.NumberFormat(
              'en-US'
            ).format(numericValue);
          }
        }
        if (property.value.salePrice) {
          const input = property.value.salePrice.replace(/,/g, '');
          const numericValue = parseFloat(input);
          if (!isNaN(numericValue)) {
            property.value.salePrice = new Intl.NumberFormat('en-US').format(
              numericValue
            );
          }
        }

        if (!property.value.percentagePremiumPaid)
          updatePercentagePremiumPaid();
        if (!property.value.salePricePerSqrM) updateSalePricePerSqm();
        if (!property.value.startingPricePerSqrM) updateStartingPricePerSqrM();

        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');
      };

      const loadAllData = async () => {
        await Promise.all([
          store.dispatch(Actions.GET_ALL_AUCTION_LOCATIONS),
          store.dispatch(Actions.GET_ALL_AUCTION_DESCRIPTIONS),
          store.dispatch(Actions.GET_ALL_AUCTION_PROPERTY_TYPES),
          store.dispatch(Actions.GET_ALL_AUCTION_STATUSES),
          store.dispatch(Actions.GET_ALL_AREAS_LIST, { type: 'auction' }),
        ]);
      };

      const validationSchema = Yup.object().shape({
        statusId: Yup.number()
          .required(() => translate('AUCTION_STATUS_IS_REQUIRED_FIELD'))
          .label('Status'),
        block: Yup.string().nullable().label('Block'),
        size: Yup.string()
          .required(() => translate('SIZE_IS_REQUIRED_FIELD'))
          .label('Size'),
        salePrice: Yup.string().nullable().label('Sale Price'),
        startingPrice: Yup.string()
          .required(() => translate('STARTING_PRICE_IS_REQUIRED_FIELD'))
          .label('Starting Price'),
        startingPricePerSqrM: Yup.string().label(
          'Starting Price Per Square Meter'
        ),
        salePricePerSqrM: Yup.string()
          .nullable()
          .label('Sale Price Per Square Meter'),
        additionalInfoEn: Yup.string()
          .nullable()
          .label('Additional Information English'),
        additionalInfoAr: Yup.string()
          .nullable()
          .label('Additional Information Arabic'),
        percentagePremiumPaid: Yup.string()
          .nullable()
          .label('Percentage Premium Paid'),
        plot: Yup.string().nullable().label('Plot'),
        attachment: Yup.mixed().label('Attachment'),
      });

      const updateStartingPricePerSqrM = async () => {
        if (
          property.value &&
          property.value.size &&
          property.value.startingPrice
        ) {
          const numericStartingPrice = property.value.startingPrice.replace(
            /[^0-9.]/g,
            ''
          );
          property.value.startingPricePerSqrM = (
            numericStartingPrice / Number(property.value.size)
          ).toFixed(2);
        }
      };

      const updateSalePricePerSqm = async () => {
        if (property.value && property.value.size && property.value.salePrice) {
          const numericSalePrice = property.value.salePrice.replace(
            /[^0-9.]/g,
            ''
          );
          property.value.salePricePerSqrM = (
            numericSalePrice / Number(property.value.size)
          ).toFixed(2);
        }
      };

      const updatePercentagePremiumPaid = async () => {
        if (
          property.value &&
          property.value.salePrice &&
          property.value.startingPrice
        ) {
          const numericStartingPrice = property.value.startingPrice.replace(
            /[^0-9.]/g,
            ''
          );
          const numericSalePricePrice = property.value.salePrice.replace(
            /[^0-9.]/g,
            ''
          );
          const difference = numericSalePricePrice - numericStartingPrice;
          property.value.percentagePremiumPaid = (
            (difference / numericStartingPrice) *
            100
          ).toFixed(2);
        }
      };

      const submit = async (values) => {
        if (property.value.areaId == 0 || property.value.propertyId == 0)
          return;

        if (!submitButtonRef.value) return;

        const selectedLocationId = property.value.locationId
          ? property.value.locationId
          : 0;
        const selectedDescriptionId = property.value.descriptionId
          ? property.value.descriptionId
          : 0;
        values.locationId = selectedLocationId;
        values.descriptionId = selectedDescriptionId;

        submitButtonRef.value.disabled = true;
        submitButtonRef.value.setAttribute('data-kt-indicator', 'on');

        let attachmentsFormData: FormData | null = null;

        if (values.attachment) {
          attachmentsFormData = new FormData();
          attachmentsFormData.append('attachment', values.attachment);
        }

        if (values.statusId != 1) {
          delete values.salePrice;
          delete values.salePricePerSqrM;
        }
        if (values.startingPrice) {
          const numericStartingPrice = values.startingPrice.replace(
            /[^0-9.]/g,
            ''
          );
          values.startingPrice = Number(numericStartingPrice).toString();
        }
        if (values.paciNo) {
          values.paciNo = Number(values.paciNo);
        }
        if (values.supplyPercentage) {
          values.supplyPercentage = Number(values.supplyPercentage);
        }
        if (values.longitude) {
          values.longitude = Number(values.longitude);
        }
        if (values.latitude) {
          values.latitude = Number(values.latitude);
        }
        if (values.salePrice) {
          const numericSalePrice = values.salePrice.replace(/[^0-9.]/g, '');
          values.salePrice = Number(numericSalePrice).toString();
        }
        values.areaId = Number(property.value?.areaId);
        values.propertyTypeId = Number(property.value?.propertyId);
        values.locationId = selectedAuctionLocation.value;
        values.descriptionId = selectedAuctionDescription.value;
        delete values.attachment;

        const data = await store.dispatch(Actions.UPDATE_AUCTION_PROPERTY, {
          data: values,
          propertyId: property.value?.id,
        });

        if (data?.id && attachmentsFormData) {
          await store.dispatch(Actions.UPLOAD_AUCTION_ATTACHMENTS, {
            data: attachmentsFormData,
            auctionId: data.id,
          });
        }

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButtonRef.value?.removeAttribute('data-kt-indicator');
          submitButtonRef.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_PROPERTY'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            showFileInput.value = false;
            setTimeout(() => {
              showFileInput.value = true;
            }, 0);

            router.push({ name: 'auctions-properties-listing' });
          });
        }
      };

      const pullData = async () => {
        if (submitPullButton.value) {
          submitPullButton.value!.disabled = true;
          submitPullButton.value.setAttribute('data-kt-indicator', 'on');
        }
        const data = await store.dispatch(
          Actions.PULL_PACI_DATA,
          Number(property.value.paciNo)
        );
        if (!data) {
          return;
        } else {
          property.value.longitude = data.attributes.lon;
          property.value.latitude = data.attributes.lat;
        }
      };

      const downloadAttachment = (item) => {
        window.open(item, '_blank');
      };

      const getAttachmentThumbnail = () => {
        return '/media/svg/files/pdf.svg';
      };

      const onStartingPriceInput = async () => {
        const input = property.value.startingPrice.replace(/,/g, '');
        const numericValue = parseFloat(input);
        if (!isNaN(numericValue)) {
          property.value.startingPrice = new Intl.NumberFormat('en-US').format(
            numericValue
          );
        }
      };

      const onSalePriceInput = async () => {
        const input = property.value.salePrice.replace(/,/g, '');
        const numericValue = parseFloat(input);
        if (!isNaN(numericValue)) {
          property.value.salePrice = new Intl.NumberFormat('en-US').format(
            numericValue
          );
        }
      };

      const goBack = () => {
        router.back();
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onStartingPriceInput,
        onSalePriceInput,
        showFileInput,
        getAttachmentThumbnail,
        downloadAttachment,
        updateStartingPricePerSqrM,
        updateSalePricePerSqm,
        updatePercentagePremiumPaid,
        property,
        translate,
        validationSchema,
        submit,
        pullData,
        selectedAuctionDescription,
        selectedAuctionLocation,
        submitButtonRef,
        submitPullButton,
        goBack,
        can,
        auctionLocations: computed(
          () => store.getters.allAuctionsLocationsList
        ),
        auctionDescriptions: computed(
          () => store.getters.allAuctionsDescriptionsList
        ),
        propertyTypes: computed(() => store.getters.allAuctionPropertyTypes),
        statuses: computed(() => store.getters.allAuctionStatuses),
        areas: computed(() => store.getters.allListAreas),
      };
    },
  });
